import { faList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createFileRoute,
  Outlet,
  useLinkProps,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Tab from 'src/components/0200_tab';
import ModSelect from 'src/components/0400_mod_select';
import useEventModSections from 'src/hooks/events/useEventModSections';
import useTargetModSection from 'src/hooks/mods/useTargetModSection';
import usePermission from 'src/hooks/permissions/usePermissions';

export const Route = createFileRoute(
  '/$organizationSlug/events/$eventId/mods/$modId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationSlug, eventId, modId } = useParams({ strict: false });
  const methods = useForm({ defaultValues: { selectedModId: Number(modId) } });
  const navigate = useNavigate();
  const { reset } = methods;
  const { findMod } = useEventModSections();
  const { targetModSection } = useTargetModSection();
  const mod = findMod(Number(modId));
  const { isPermitted: canAssistMod } = usePermission({
    action: 'assists_mod',
    eventId: Number(eventId),
  });

  const linkToWarRoom = useLinkProps({
    to: '/$organizationSlug/events/$eventId/mods/$modId',
    params: {
      organizationSlug: String(organizationSlug),
      eventId: String(eventId),
      modId: String(modId),
    },
  });
  const linkToRewards = useLinkProps({
    to: '/$organizationSlug/events/$eventId/mods/$modId/rewards',
    params: {
      organizationSlug: String(organizationSlug),
      eventId: String(eventId),
      modId: String(modId),
    },
  });
  const linkToMods = useLinkProps({
    to: '/$organizationSlug/events/$eventId/mods',
    params: {
      organizationSlug: String(organizationSlug),
      eventId: String(eventId),
    },
  });
  const linkToStoryteller = useLinkProps({
    to: '/$organizationSlug/events/$eventId/mods/$modId/storyteller',
    params: {
      organizationSlug: String(organizationSlug),
      eventId: String(eventId),
      modId: String(modId),
    },
  });

  useEffect(() => {
    if (modId) {
      reset({ selectedModId: Number(modId) });
    }
  }, [modId, reset]);

  return (
    <FormProvider {...methods}>
      <div className="border-b border-juno-gray-700 pb-0 pt-2 mb-4 sticky top-0 z-20 gray-box">
        <div className="flex sm:justify-center items-center gap-4">
          <div className="flex items-center">
            <Tab
              label={
                <FontAwesomeIcon icon={faList} className="fa-fw pt-1 pb-1" />
              }
              highlightMode="horizontal"
              to={linkToMods.href}
            />

            {canAssistMod ? (
              <ModSelect
                width="w-72"
                name="selectedModId"
                selectedValue={Number(modId)}
                onSelectionChange={x => {
                  if (Number(x) > 0) {
                    navigate({
                      to: targetModSection,
                      params: {
                        organizationSlug: String(organizationSlug),
                        eventId: String(eventId),
                        modId: String(x),
                      },
                    });
                  }
                }}
              />
            ) : (
              <div className="whitespace-nowrap pb-[5px] px-2">{mod?.name}</div>
            )}

            {canAssistMod && (
              <>
                <Tab
                  label="War Room"
                  highlightMode="horizontal"
                  to={linkToWarRoom.href}
                />

                <Tab
                  label="Storyteller"
                  highlightMode="horizontal"
                  to={linkToStoryteller.href}
                />
              </>
            )}

            <Tab
              label="Rewards"
              highlightMode="horizontal"
              to={linkToRewards.href}
            />
          </div>
        </div>
      </div>
      <Outlet />
    </FormProvider>
  );
}
